import { find, get, map, maxBy } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Subscription from "shared/components/Subscription";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { UPDATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  COMMISSION_TYPES,
  INSURANCE_PLAN_PARAMS_RULES,
  PROCESS_TAGS,
  SCREENS,
  SUBSCRIPTION_STATUSES,
  THEMES_PALETTE,
} from "utils/constants";
import GenerateLinkModal from "./GenerateLinkModal";
import { isProspectLinkDisabled } from "./GenerateLinkModal/generateLinkOpen";

const Subscriptions = ({
  translation,
  data,
  project,
  tracers,
  onChangeStatus,
  user,
}) => {
  const navigate = useNavigate();
  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);
  const [open, setOpen] = useState({});
  const [getTracer] = useLazyQuery(TRACER);
  const matchStatusesWithProcess = (statuses) => {
    const result = map(
      statuses.filter(({ status }) => status),
      ({ status: { id: statusId, order } }) => {
        const allProcessStatuses = tracers.flatMap(
          (tracer) =>
            tracer?.flow?.process?.processStatuses
              ?.filter(({ id }) => id === statusId)
              .map((status) => ({
                ...status,
                tracer,
              })) || [],
        );
        const matchedStatus = maxBy(allProcessStatuses, (candidate) => {
          return -Math.abs(candidate.order - order);
        });

        return {
          tracer: matchedStatus?.tracer || null,
          processStatus: matchedStatus || null,
        };
      },
    ).filter(({ tracer }) => tracer !== null);
    return maxBy(result, ({ processStatus: { order } }) => parseInt(order, 10));
  };
  const onClick = (id) => {
    if (find(data, { id })?.status === SUBSCRIPTION_STATUSES.DRAFT) {
      const statusData = matchStatusesWithProcess(get(project, "statuses"));
      const subscriptionInProgress = find(
        get(statusData, "tracer.flow.process.processStatuses", []),
        { tag: PROCESS_TAGS.CURRENT_SUBSCRIPTION },
      );
      onChangeStatus({ project, status: subscriptionInProgress });
      updateSubscription({
        variables: {
          where: {
            id,
          },
          data: { status: SUBSCRIPTION_STATUSES.IN_PROGRESS },
        },
      });
    }
    navigate(`${SCREENS.PRIVATE.BROKER.SUBSCRIPTIONS.DETAIL.path}/${id}`);
  };

  const onOpenGenerateLink = (data) => setOpen(data);

  return (
    <>
      <GenerateLinkModal
        open={open}
        setOpen={setOpen}
        template={get(project, "fields.processTemplate")}
        tracerId={open?.tracerId}
      />
      {data
        .flatMap((subscription) => {
          const subscriptionProject = get(subscription, "project");
          const insurancePlan = get(subscription, "insurancePlan");
          const baseId = get(insurancePlan, "id");
          const allCombinations = get(
            subscriptionProject,
            "fields.combinations",
            [],
          );
          const baseCombinations = allCombinations.filter(
            (combination) => combination.baseInsurancePlanId === baseId,
          );
          if (baseCombinations.length === 0)
            return [{ subscription, combination: null, complement: null }];
          const upsellsByBase = get(subscriptionProject, "fields.upsells", {});
          const baseUpsells = get(upsellsByBase, baseId, []);
          return baseCombinations.map((combination) => {
            const complement = combination.complementaryInsurancePlanId
              ? find(
                  baseUpsells,
                  (module) =>
                    get(module, "insurancePlan.id") ===
                    combination.complementaryInsurancePlanId,
                )
              : null;
            return { subscription, combination, complement };
          });
        })
        .map(({ subscription, combination, complement }) => {
          const {
            id,
            status,
            insurancePlan,
            project: subscriptionProject,
            additionalInfo,
          } = subscription;
          const { cost, currency, commission } = find(
            get(subscriptionProject, "generalCosts"),
            (cost) =>
              get(cost, "insurancePlan.id") === get(insurancePlan, "id"),
          );
          // When the project carries Mobilité Mutuelle combinations, render
          // ONE card per combination (Socle alone or Socle + Confort) with
          // the per-combination cost and upsell list. Otherwise fall back
          // to the legacy "one card per subscription, all upsells grouped".
          const isCombinationEntry = !!combination;
          const upsellsForCard = isCombinationEntry
            ? complement
              ? [complement]
              : []
            : get(
                subscriptionProject,
                `fields.upsells.${insurancePlan.id}`,
                [],
              );
          const totalCost = isCombinationEntry
            ? (cost + get(complement, "cost", 0)).toFixed(2)
            : get(subscriptionProject, `fields.upsells.${insurancePlan.id}`, [])
                ?.reduce((acc, curr) => acc + curr.cost, cost)
                ?.toFixed(2);
          const expired =
            subscriptionProject.expired ||
            get(insurancePlan, "params", []).some((param) => {
              return (
                moment(get(subscriptionProject, "contract.issuanceDate"))
                  .startOf("day")
                  .diff(moment().startOf("day"), "days") <
                get(param, "rules", []).find(
                  ({ name }) =>
                    name ===
                    (get(subscriptionProject, "ria")
                      ? INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA
                      : INSURANCE_PLAN_PARAMS_RULES.DELTA_START),
                )?.value
              );
            });

          const entryKey = isCombinationEntry ? `${id}__${combination.id}` : id;

          return (
            <Subscription
              key={entryKey}
              tracers={tracers}
              translation={translation.SUBSCRIPTION}
              date={get(subscriptionProject, "contract.issuanceDate")}
              user={`${get(project, "contact.user.firstname")} ${get(
                project,
                "contact.user.lastname",
              )}`}
              status={translation.SUBSCRIPTION.STATUSES[status]}
              product={get(insurancePlan, "name")}
              category={get(insurancePlan, "description")}
              icon={get(insurancePlan, "icon")}
              cost={totalCost}
              upsells={upsellsForCard}
              period={translation.MONTH}
              currency={currency}
              onClick={() => onClick(id)}
              disabled={{
                status: expired,
                text: translation.SUBSCRIPTION.POPOVER,
              }}
              tag={
                get(commission, "type") === COMMISSION_TYPES.LINEAR
                  ? `${get(commission, "value", "-")}/${get(
                      commission,
                      "value",
                      "-",
                    )}`
                  : `${get(commission, "value", "-")}/${get(
                      commission,
                      "withholding",
                      "-",
                    )}`
              }
              disabledGenerateLink={isProspectLinkDisabled({
                user,
                processTemplate: get(
                  subscriptionProject,
                  "fields.processTemplate",
                ),
              })}
              onOpenGenerateLink={async () => {
                const { data: tracerData } = await getTracer({
                  variables: {
                    where: {
                      flow: {
                        process: {
                          theme:
                            THEMES_PALETTE[
                              get(subscriptionProject, "fields.processTemplate")
                            ]?.PROSPECT_PROCESS,
                        },
                      },
                    },
                  },
                });
                const tracer = tracerData?.tracer;
                const sourceId = get(tracer, "source.id");
                if (!sourceId) {
                  return;
                }
                onOpenGenerateLink({
                  projectId: subscriptionProject?.id,
                  subscriptionId: id,
                  insurancePlanIds: [insurancePlan?.id],
                  combinationId: combination?.id,
                  complementaryInsurancePlanId:
                    combination?.complementaryInsurancePlanId || null,
                  projectFields: get(subscriptionProject, "fields", {}),
                  isInfoCompleted: additionalInfo?.isInfoCompleted,
                  isSigned: status === SUBSCRIPTION_STATUSES.SIGNED,
                  tracerId: get(project, "tracer.id"),
                  initialProjectStatuses: get(project, "statuses", []),
                  baseUrl: get(
                    insurancePlan,
                    "config.generatedUrl",
                    `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${sourceId}`,
                  ),
                });
              }}
            />
          );
        })}
    </>
  );
};

export default Subscriptions;
